import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import {
  IconArrowBackUp,
  IconArrowLeft,
  IconBook,
  IconDeviceDesktop,
  IconDownload,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react"

interface Tag {
  id: number
  name: string
}

interface CurrentCheckout {
  id: number
  user_name: string
  user_id: number
  checked_out_at: string
  due_date: string | null
  overdue: boolean
  days_overdue: number
  days_until_due: number | null
}

interface CheckoutRecord {
  id: number
  user_name: string
  checked_out_at: string
  returned_at: string | null
  status: string
}

interface Book {
  id: number
  title: string
  author: string
  book_type: string
  description: string | null
  isbn: string | null
  publisher: string | null
  year_published: number | null
  pages: number | null
  size_in_mb: number | null
  created_at: string
  owner_name: string
  tags: Tag[]
  has_pdf: boolean
  download_url: string | null
  available_for_checkout: boolean
  currently_checked_out: boolean
  current_checkout: CurrentCheckout | null
  checkout_history: CheckoutRecord[]
  can_update: boolean
  can_destroy: boolean
  can_checkout: boolean
}

interface Props {
  [key: string]: unknown
  book: Book
  current_user_id: number
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const CHECKOUT_STATUS_BADGE: Record<
  string,
  { variant: "default" | "secondary" | "destructive" | "outline"; label: string }
> = {
  active: { variant: "default", label: "Active" },
  returned: { variant: "outline", label: "Returned" },
  overdue: { variant: "destructive", label: "Overdue" },
}

export default function BookShow() {
  const { book, current_user_id } = usePage<Props>().props

  function handleCheckout() {
    router.post(`/books/${book.id}/checkouts`)
  }

  function handleReturn() {
    if (!book.current_checkout) return
    if (confirm("Return this book?")) {
      router.patch(`/checkouts/${book.current_checkout.id}/return`)
    }
  }

  function handleDelete() {
    if (confirm("Are you sure you want to delete this book?")) {
      router.delete(`/books/${book.id}`)
    }
  }

  return (
    <>
      <Head title={`${book.title} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        {/* Header */}
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/books">Library</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{book.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a href="/books">
                  <IconArrowLeft className="h-4 w-4" />
                </a>
              </Button>
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-lg font-semibold tracking-tight">{book.title}</h1>
                  {book.book_type === "digital" ? (
                    <Badge variant="secondary">
                      <IconDeviceDesktop className="mr-1 h-3 w-3" />
                      Digital
                    </Badge>
                  ) : book.available_for_checkout ? (
                    <Badge variant="default">Available</Badge>
                  ) : (
                    <Badge variant="destructive">Checked Out</Badge>
                  )}
                </div>
                <p className="text-muted-foreground text-sm">by {book.author}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Download PDF */}
              {book.has_pdf && book.download_url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={book.download_url}>
                    <IconDownload className="mr-1.5 h-4 w-4" />
                    Download PDF
                  </a>
                </Button>
              )}

              {/* Checkout / Return */}
              {book.book_type === "physical" && book.available_for_checkout && book.can_checkout && (
                <Button size="sm" onClick={handleCheckout}>
                  <IconBook className="mr-1.5 h-4 w-4" />
                  Check Out
                </Button>
              )}
              {book.current_checkout && book.current_checkout.user_id === current_user_id && (
                <Button variant="outline" size="sm" onClick={handleReturn}>
                  <IconArrowBackUp className="mr-1.5 h-4 w-4" />
                  Return Book
                </Button>
              )}

              {/* Edit / Delete */}
              {book.can_update && (
                <Button variant="outline" size="sm" asChild>
                  <a href={`/books/${book.id}/edit`}>
                    <IconPencil className="mr-1.5 h-4 w-4" />
                    Edit
                  </a>
                </Button>
              )}
              {book.can_destroy && (
                <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={handleDelete}>
                  <IconTrash className="mr-1.5 h-4 w-4" />
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* Main */}
          <div className="xl:col-span-2 space-y-6">
            {/* Description */}
            {book.description && (
              <div className="rounded-xl border bg-card p-5">
                <p className="text-muted-foreground mb-1.5 text-xs font-medium uppercase tracking-wider">Description</p>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{book.description}</p>
              </div>
            )}

            {/* Tags */}
            {book.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {book.tags.map((tag) => (
                  <a key={tag.id} href={`/books?tag_id=${tag.id}`}>
                    <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80">
                      {tag.name}
                    </Badge>
                  </a>
                ))}
              </div>
            )}

            {/* Current Checkout */}
            {book.current_checkout && (
              <div className="rounded-xl border border-destructive/20 bg-destructive/5">
                <div className="p-5">
                  <p className="text-sm font-medium">Currently Checked Out</p>
                  <div className="mt-3 grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">By</p>
                      <p className="mt-1 text-sm font-medium">{book.current_checkout.user_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">Since</p>
                      <p className="mt-1 text-sm font-medium">{formatDate(book.current_checkout.checked_out_at)}</p>
                    </div>
                    {book.current_checkout.due_date && (
                      <div>
                        <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">Due</p>
                        <p className="mt-1 text-sm font-medium">
                          {formatDate(book.current_checkout.due_date)}
                          {book.current_checkout.overdue && (
                            <Badge variant="destructive" className="ml-2 text-xs">
                              {book.current_checkout.days_overdue}d overdue
                            </Badge>
                          )}
                          {!book.current_checkout.overdue &&
                            book.current_checkout.days_until_due !== null &&
                            book.current_checkout.days_until_due <= 7 && (
                              <Badge variant="secondary" className="ml-2 text-xs">
                                {book.current_checkout.days_until_due}d left
                              </Badge>
                            )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Checkout History */}
            {book.checkout_history.length > 0 && (
              <div className="rounded-xl border bg-card">
                <div className="p-5 pb-0">
                  <h3 className="text-sm font-medium">Checkout History</h3>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Checked Out</TableHead>
                        <TableHead>Returned</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {book.checkout_history.map((checkout) => {
                        const statusBadge = CHECKOUT_STATUS_BADGE[checkout.status] || {
                          variant: "outline" as const,
                          label: checkout.status,
                        }
                        return (
                          <TableRow key={checkout.id}>
                            <TableCell className="font-medium">{checkout.user_name}</TableCell>
                            <TableCell className="tabular-nums">{formatDate(checkout.checked_out_at)}</TableCell>
                            <TableCell className="tabular-nums">
                              {checkout.returned_at ? formatDate(checkout.returned_at) : "\u2014"}
                            </TableCell>
                            <TableCell>
                              <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Details */}
            <div className="rounded-xl border bg-card p-5">
              <h3 className="mb-3 text-sm font-medium">Details</h3>
              <dl className="space-y-2.5 text-sm">
                <MetaRow label="Type" value={book.book_type === "digital" ? "Digital" : "Physical"} />
                {book.isbn && <MetaRow label="ISBN" value={book.isbn} />}
                {book.publisher && <MetaRow label="Publisher" value={book.publisher} />}
                {book.year_published && <MetaRow label="Year" value={String(book.year_published)} />}
                {book.pages && <MetaRow label="Pages" value={String(book.pages)} />}
                {book.size_in_mb && <MetaRow label="File Size" value={`${book.size_in_mb} MB`} />}
                <Separator />
                <MetaRow label="Added by" value={book.owner_name} />
                <MetaRow label="Added" value={formatDate(book.created_at)} />
              </dl>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}

BookShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
